/**
 * lib/bookletPdf.ts
 * ------------------------------------------------------------------
 * Booklet imposition for bulk printing. Takes the normal A5 report PDF
 * (every student = exactly 4 pages, in reading order 1-2-3-4) and re-lays
 * it out as an A4 LANDSCAPE, 2-up booklet:
 *
 *   Each student -> ONE A4 sheet = 2 A4-landscape pages
 *     Sheet FRONT: [ page 4 (back) | page 1 (cover) ]
 *     Sheet BACK:  [ page 2        | page 3         ]
 *
 * Printed double-sided (flip on the SHORT edge) and folded down the middle,
 * the sheet reads cover → 2 → 3 → back — a proper A5 saddle-fold booklet.
 * Two A5 pages fit an A4 landscape sheet exactly (same aspect), so there is
 * no scaling distortion. For N students the output has 2N A4 pages.
 *
 * Uses pdf-lib (page embedding) so the existing jsPDF report rendering is
 * reused untouched — we only re-impose the finished pages.
 * ------------------------------------------------------------------ */
import { PDFDocument } from 'pdf-lib';

// A4 landscape in PDF points (72 dpi): 297 x 210 mm.
const A4L_W = 841.89;
const A4L_H = 595.28;

/** Re-impose a 4-pages-per-student A5 report PDF into an A4 landscape booklet. */
export async function imposeBooklet(a5PdfBytes: ArrayBuffer | Uint8Array): Promise<Uint8Array> {
  const src = await PDFDocument.load(a5PdfBytes);
  const out = await PDFDocument.create();
  const total = src.getPageCount();
  const half = A4L_W / 2;

  const place = (page: import('pdf-lib').PDFPage, embedded: import('pdf-lib').PDFEmbeddedPage, slot: 0 | 1) => {
    page.drawPage(embedded, { x: slot * half, y: 0, width: half, height: A4L_H });
  };

  for (let base = 0; base + 3 < total; base += 4) {
    const [p1, p2, p3, p4] = await out.embedPages([
      src.getPage(base),
      src.getPage(base + 1),
      src.getPage(base + 2),
      src.getPage(base + 3),
    ]);
    const front = out.addPage([A4L_W, A4L_H]);
    place(front, p4, 0); // back cover on the left
    place(front, p1, 1); // front cover on the right
    const back = out.addPage([A4L_W, A4L_H]);
    place(back, p2, 0);
    place(back, p3, 1);
  }

  // Defensive: reports are always 4 pages, but if a stray remainder exists,
  // append it 2-up as-is rather than dropping pages.
  const rem = total % 4;
  if (rem) {
    for (let i = total - rem; i < total; i += 2) {
      const idxs = [src.getPage(i)];
      if (i + 1 < total) idxs.push(src.getPage(i + 1));
      const emb = await out.embedPages(idxs);
      const pg = out.addPage([A4L_W, A4L_H]);
      emb.forEach((e, j) => place(pg, e, (j % 2) as 0 | 1));
    }
  }

  return out.save();
}
